import { config } from "../config";
import { callGateway, type CallerIdentity } from "./gateway";
import type { PlanRow } from "../domain/migrationSplit";

/**
 * A file's plans against this operator's plans (owner 2026-11-01). radagent
 * compares by numbers, no model; an existing plan is never changed.
 */
export interface ExistingPlan { groupname: string; price: number | null; devices: number; on_sale: boolean }

export interface ComparedPlan {
  source_name: string;
  price?: number;
  devices?: number;
  match?: "same" | "price_differs" | "new";
  existing?: ExistingPlan[];
  problem?: string;
}

/** use:<groupname> | special | new-on | new-off | "" (not chosen yet). */
export type PlanChoice = string;

export function defaultChoice(c: ComparedPlan): PlanChoice {
  if (c.problem) return "";
  if (c.match === "same") return c.existing?.length === 1 ? `use:${c.existing[0]!.groupname}` : "";
  return c.match === "price_differs" ? "special" : "new-off";
}

export function problemText(problem: string): string {
  return problem === "price_includes_tax" ? "Say in the file whether this price includes GST (prices_include_gst: yes or no)." : problem;
}

export interface MigratePlan { rows: Array<PlanRow & { kind: "special" | "new"; on_sale: boolean }>; matches: Array<{ source_name: string; groupname: string }> }

export function planFrom(rows: PlanRow[], choices: Record<string, PlanChoice>): MigratePlan {
  const out: MigratePlan = { rows: [], matches: [] };
  for (const r of rows) {
    const c = choices[r.source_name] ?? "";
    if (c.startsWith("use:")) out.matches.push({ source_name: r.source_name, groupname: c.slice(4) });
    else if (c === "special") out.rows.push({ ...r, kind: "special", on_sale: false });
    else if (c === "new-on" || c === "new-off") out.rows.push({ ...r, kind: "new", on_sale: c === "new-on" });
  }
  return out;
}

const base = (ns: string) => `/radagent/migrate/${encodeURIComponent(ns)}`;

export async function comparePlans(i: CallerIdentity, ns: string, rows: PlanRow[]): Promise<ComparedPlan[]> {
  const r = await callGateway<{ rows: ComparedPlan[] }>(i, config().gatewayBase, `${base(ns)}/compare`, { method: "POST", body: { namespace: ns, rows } });
  return r.data.rows;
}

export interface AppliedPlan { source_name: string; groupname?: string; created?: boolean; error?: string }

/** Starts creating the plans; the gateway's time limit means it answers at once. */
export async function applyPlans(i: CallerIdentity, ns: string, rows: MigratePlan["rows"]): Promise<string> {
  const r = await callGateway<{ id: string }>(i, config().gatewayBase, `${base(ns)}/apply`, { method: "POST", body: { namespace: ns, rows } });
  return r.data.id;
}

export async function readApply(i: CallerIdentity, ns: string, id: string): Promise<{ status: string; rows?: AppliedPlan[]; error?: string }> {
  const r = await callGateway<{ status: string; rows?: AppliedPlan[]; error?: string }>(i, config().gatewayBase, `${base(ns)}/${encodeURIComponent(id)}`);
  return r.data;
}

export const saveMatches = (i: CallerIdentity, ns: string, matches: MigratePlan["matches"]) =>
  callGateway<{ saved: number }>(i, config().gatewayBase, `/operator/${encodeURIComponent(ns)}/import/plan-matches`, { method: "POST", body: { matches } });
